import classNames from 'classnames';
import type { FC } from 'react';

import { Gtm } from '@/core/tracking/utils/Gtm';
import { FilterExplanation } from '@/domains/productDiscovery/Listings/components/Filters/components/FilterExplanation/FilterExplanation';
import { ecoExplanationText } from '@/domains/productDiscovery/Listings/components/Filters/components/FilterExplanation/filterExplanationText';
import { Label } from '@/domains/productDiscovery/Listings/components/Filters/components/Label/Label';
import { Checkbox } from '@/domains/productDiscovery/Listings/components/Filters/components/newTamagoshiComponents/Checkbox/Checkbox';
import { EcoLabel } from '@/productDiscovery/Listings/components/EcoLabel/EcoLabel';
import { useFacet } from '@/productDiscovery/Listings/facets/hooks/useFacet';
import type { FilterConfig } from '@/productDiscovery/Listings/interfaces/filterConfig';
import { useCategoryFamilyId } from '@/productDiscovery/Listings/pages/Category/hooks/useCategoryFamilyId';
import { useCategoryId } from '@/productDiscovery/Listings/pages/Category/hooks/useCategoryId';
import { useCategoryLevel } from '@/productDiscovery/Listings/pages/Category/hooks/useCategoryLevel';

import styles from './EcoFilter.module.scss';

interface Props {
  filterConfig: FilterConfig;
  className?: string;
  filterCount?: number;
  position?: number;
}

export const EcoFilter: FC<Props> = ({
  filterConfig,
  className,
  filterCount,
  position,
}) => {
  const categoryId = useCategoryId();
  const categoryLevel = useCategoryLevel();
  const categoryFamilyId = useCategoryFamilyId();

  const { facet, refineFacetValue } = useFacet<'bool'>('is_eco_responsible');

  const filter = facet?.values.find(({ name }) => name === 'true');

  const filterItem = {
    id: filterConfig.attributeName,
    label: filterConfig.translations.label as string,
    isRefined: !!filter?.isRefined,
    value: String(filter?.isRefined || false),
    count: filter?.count || 0,
  };

  const isDisabled = filterItem.count === 0;

  const handleChange = async () => {
    refineFacetValue(!filter?.isRefined);

    const { generateFiltersTracking } = await import(
      '@/domains/productDiscovery/Listings/components/Filters/tracking/generateFiltersTracking'
    );

    Gtm.push(
      generateFiltersTracking({
        e_label: filterItem.value,
        filter_id: filterItem.id,
        filter_name: filterItem.id,
        filter_value: filterItem.value,
        filter_position: position,
        filter_count: filterCount,
        category_id: categoryId,
        category_level: categoryLevel,
        family_id: categoryFamilyId,
      }),
    );
  };

  return (
    <div className={classNames(styles.root, className)}>
      <Checkbox
        id="is_eco_responsible"
        isChecked={filterItem.isRefined}
        value={filterItem.isRefined}
        onClick={handleChange}
        isDisabled={isDisabled}
      >
        <Label
          label={<EcoLabel />}
          disabled={isDisabled}
          active={filterItem.isRefined}
        />
      </Checkbox>

      <FilterExplanation
        label="En savoir plus"
        filterExplanationText={ecoExplanationText}
        attributeName={filterConfig.attributeName}
        className={styles.ecoExplanationText}
      />
    </div>
  );
};
